import { AxiosError } from "axios";
import { useHistory } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import useBasePath from "src/utils/hooks/useBasePath";
import { dynamicNotification } from "src/utils/Notifications";
import __ from "src/utils/Translations";
import {
  fetchAbsenceGroups,
  fetchAbsenceGroup,
  createAbsenceGroup,
  editAbsenceGroup,
  changeStatusAbsenceGroup,
} from "src/Pages/Company/Absences/api";
import {
  IAbsenceGroup,
  ICustomErrorDetails,
  INewAbsenceGroup,
} from "src/Pages/Company/Absences/AbsenceGroups/AbsenceGroups.models";
import { IFormValues } from "src/Pages/Company/Absences/AbsenceGroups/AbsenceGroupsEdit";

export const useFetchAbsenceGroups = (companyId: string) =>
  useQuery<IAbsenceGroup[], Error>({
    queryKey: ["absence-groups", { companyId }],
    queryFn: async ({ signal }) => fetchAbsenceGroups({ companyId, signal }),
    initialData: [],
  });

export const useFetchAbsenceGroup = (groupId: string, isNew: boolean) =>
  useQuery<IAbsenceGroup, Error>({
    enabled: !isNew,
    queryKey: ["absence-group", { groupId }],
    queryFn: async ({ signal }) => fetchAbsenceGroup({ groupId, signal }),
  });

interface ISaveAbsenceGroupMutation {
  companyId: string;
  groupId: string;
  isNew: boolean;
  active?: boolean;
}

interface IAbsenceGroupStatusArgs {
  groupId: string;
  groupName: string;
  active: boolean;
}

export const useSaveAbsenceGroup = ({
  companyId,
  groupId,
  isNew,
  active,
}: ISaveAbsenceGroupMutation) => {
  const history = useHistory();
  const queryClient = useQueryClient();
  const basePath = useBasePath();

  const mutation = useMutation({
    mutationFn: async (formData: IFormValues) => {
      const parsedFormData: INewAbsenceGroup = {
        ...formData,
        ownerId: companyId,
        active: isNew || !!active,
      };

      if (!isNew) {
        parsedFormData.id = groupId;
      }

      const result = isNew
        ? await createAbsenceGroup(parsedFormData)
        : await editAbsenceGroup(groupId, parsedFormData);
      return result;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["absence-group", { groupId }],
      });

      dynamicNotification(__("Pomyślnie zapisano grupę nieobecności."));

      // A bit of a hack to ensure all state updates are done before redirecting
      // Used for confirmation modal
      setTimeout(() => {
        history.push(`${basePath}/absences/absence-groups`);
      }, 500);
    },
    onError: () => {
      dynamicNotification(
        __("Nie udało się zapisać danych."),
        "error",
        "top-right",
        7000
      );
    },
  });

  return mutation;
};

export const useChangeStatusAbsenceGroup = () =>
  useMutation<
    unknown,
    AxiosError<ICustomErrorDetails>,
    IAbsenceGroupStatusArgs
  >({
    mutationFn: async ({ groupId, active }: IAbsenceGroupStatusArgs) => {
      const result = await changeStatusAbsenceGroup(groupId, active);

      return result;
    },
  });

export const useValidateAbsenceGroupName = () => {
  const queryClient = useQueryClient();

  const validateGroupName = async (
    companyId: string,
    groupName: string,
    excludeGroupId?: string
  ): Promise<boolean> => {
    const groupsData: IAbsenceGroup[] = await queryClient.fetchQuery({
      queryKey: ["absence-groups", { companyId }],
      queryFn: async ({ signal }) =>
        fetchAbsenceGroups({ companyId, name: groupName, signal }),
    });

    return groupsData.some((group) => group.id !== excludeGroupId);
  };

  return { validateGroupName };
};
